<template>
  <div v-if="compareToUserId(allRide?.ride?.authorId)" class="trip-calendar-section" :dir="isHebrew ? 'rtl' : 'ltr'">
    <div class="calendar-instructions">
      <div class="instruction-item">
        <i class="fas fa-arrows-alt"></i>
        <span>{{ t('tripCalendarDragAttractions') }}</span>
      </div>
      <div class="instruction-item">
        <i class="fas fa-mouse-pointer"></i>
        <span>{{ t('tripCalendarClickDayAddActivities') }}</span>
      </div>
      <div class="instruction-item">
        <i class="material-icons">schedule</i>
        <span>{{ t('tripCalendarClickClockSetTime') }}</span>
      </div>
    </div>
    <!-- Reaches every date the API accepts, including the week either side
         of the trip, which the calendar itself does not render as cells.
         Anything created out there shows in the before/after blocks around
         the grids. Same gate and same modal as the per-day affordance. -->
    <div v-if="canManageOrganizerMessages" class="organizer-message-create-row">
      <button
        type="button"
        class="organizer-message-create-button"
        @click="openCreateOrganizerMessage()"
      >
        <i class="material-icons">add_comment</i>
        <span>{{ t('organizerMessageCreateButton') }}</span>
      </button>
    </div>
    <!-- Messages this calendar structurally CANNOT show, both blocks
         stacked above the grids it can (owner's call, 2026-10-05: one
         place to look, and the same order as the list view).
         tripMonths renders only the months the trip spans and
         filterEventsForMonth drops anything outside them, so a message
         scheduled in an adjacent month has no cell to appear in at all --
         reachable for any trip near a month boundary once the window
         widened from 2 days to 7. It was still scheduled and still billed
         per traveller, and the list view's block was gated behind
         !showCalendarView, so from this tab it was invisible AND
         undeletable. Same component, modal and delete as the list view. -->
    <OrganizerMessageList
      v-if="canManageOrganizerMessages"
      :messages="organizerMessagesBeforeTrip"
      :title="t('organizerMessageBeforeTripTitle')"
      show-date
      @edit="$refs.organizerMessageModal.openForMessage($event)"
      @remove="deleteOrganizerMessage($event)"
    />
    <OrganizerMessageList
      v-if="canManageOrganizerMessages"
      :messages="organizerMessagesAfterTrip"
      :title="t('organizerMessageAfterTripTitle')"
      show-date
      @edit="$refs.organizerMessageModal.openForMessage($event)"
      @remove="deleteOrganizerMessage($event)"
    />

    <div class="calendars-container">
      <div
        v-for="month in tripMonths"
        :key="month.date"
        class="calendar-wrapper"
      >
        <FullCalendar
          ref="calendar"
          :options="getCalendarOptions(month.date)"
          class="trip-calendar" />
      </div>
    </div>

    <OrganizerMessageModal
      v-if="canManageOrganizerMessages"
      ref="organizerMessageModal"
      @saved="upsertOrganizerMessage"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import FullCalendar from '@fullcalendar/vue';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import moment from 'moment';
import { translationMixin } from '@/mixins/translationMixin';
import { organizerMessagesMixin } from '@/mixins/organizerMessagesMixin';
import { filterEventsForMonth } from '@/utils/tripCalendarFiltering';
import OrganizerMessageModal from '@/components/OrganizerMessageModal.vue';
import OrganizerMessageList from '@/components/OrganizerMessageList.vue';

export default {
  name: 'TripCalendarView',
  mixins: [translationMixin, organizerMessagesMixin],
  components: {
    FullCalendar,
    OrganizerMessageModal,
    OrganizerMessageList
  },
  data() {
    return {};
  },
  computed: {
    ...mapGetters([
      'allRide',
      'compareToUserId',
      'newAttractionDateTime',
      'tripViewMode',
      'isUserManager',
      'isNotMobile',
      'isRideLocked',
      // Set by the day menu; watched below to open the scheduled-message
      // modal for the day the manager picked.
      'organizerMessageDayRequest',
      // userId and apptoken are NOT mapped here: organizerMessagesMixin maps
      // both, and this component no longer references either directly.
    ]),

    tripMonths() {
      if (!this.allRide?.ride?.start_date || !this.allRide?.ride?.end_date) {
        return [{ date: moment().format('YYYY-MM-DD') }];
      }

      const startDate = moment(this.allRide.ride.start_date, 'DD-MM-YYYY');
      const endDate = moment(this.allRide.ride.end_date, 'DD-MM-YYYY');
      
      // Calculate how many months the trip spans
      const months = [];
      let currentDate = startDate.clone().startOf('month');
      
      while (currentDate.isSameOrBefore(endDate, 'month')) {
        months.push({
          date: currentDate.format('YYYY-MM-DD')
        });
        currentDate.add(1, 'month');
      }

      return months;
    }
  },
  methods: {
    ...mapActions([
      'editAttraction',
      'updateAttractionTimeAtRide',
      'reorderAttractionsInDay',
      'rideUpdateMarkerByScrollerLocation',
      'rideUpdateMarkerByDayIndex',
      'showModal',
      'showModalWithParameters',
      'lowedAttractionAndShow',
      'goToRideAccommodation',
      'goToBaseRideAccommodation',
    ]),

    updateCalendarDates() {
      // Force calendar to refresh with new date constraints
      if (this.$refs.calendar) {
        // If multiple calendars exist (array of refs)
        if (Array.isArray(this.$refs.calendar)) {
          this.$refs.calendar.forEach(calendar => {
            if (calendar) {
              const calendarApi = calendar.getApi();
              // Update the calendar's date constraints
              calendarApi.setOption('selectConstraint', {
                start: this.allRide?.ride?.start_date ? moment(this.allRide.ride.start_date, 'DD-MM-YYYY').format('YYYY-MM-DD') : null,
                end: this.allRide?.ride?.end_date ? moment(this.allRide.ride.end_date, 'DD-MM-YYYY').add(1, 'days').format('YYYY-MM-DD') : null
              });
              // Force a re-render of the calendar
              calendarApi.render();
            }
          });
        }
      }
      
      // Update all day cells' styling
      this.$nextTick(() => {
        const dayCells = document.querySelectorAll('.fc-day');
        dayCells.forEach(cell => {
          const cellDate = moment(cell.getAttribute('data-date'));
          const startDate = moment(this.allRide?.ride?.start_date, 'DD-MM-YYYY');
          const endDate = moment(this.allRide?.ride?.end_date, 'DD-MM-YYYY');

          cell.classList.remove('outside-trip-range');
          if (cellDate.isBefore(startDate) || cellDate.isAfter(endDate)) {
            cell.classList.add('outside-trip-range');
          }
        });
      });
    },

    getEventsForMonth(monthDate) {
      return filterEventsForMonth(
        [...this.getEventsFromAttractions(), ...this.getOrganizerMessageEvents()],
        monthDate
      );
    },

    getCalendarOptions(monthDate) {
      return {
        plugins: [dayGridPlugin, interactionPlugin],
        initialView: 'dayGridMonth',
        initialDate: monthDate,
        headerToolbar: {
          left: 'prev,next',
          center: 'title',
          right: ''
        },
        showNonCurrentDates: false,
        events: this.getEventsForMonth(monthDate),
        editable: this.compareToUserId(this.allRide?.ride?.authorId) && !this.isRideLocked,
        droppable: !this.isRideLocked,
        selectable: !this.isRideLocked,
        longPressDelay: 300,
        direction: this.isHebrew ? 'rtl' : 'ltr',
        locale: this.isHebrew ? 'he' : 'en',
        firstDay: 0,
        eventClick: this.handleEventClick,
        dateClick: this.handleDateClick,
        height: 'auto',
        eventDragStart: this.handleEventDragStart,
        eventDrop: this.handleEventDrop,
        eventReceive: this.handleEventReceive,
        dropAccept: '.fc-event',
        dragRevertDuration: 0,
        eventOrder: 'order',
        eventContent: this.renderEventContent,
        eventResizableFromStart: false,
        eventDurationEditable: false,
        dayCellDidMount: this.handleDayCellMount,
        eventDidMount: this.handleEventDidMount,
        eventDragStop: this.handleEventDragStop,
        // Add validation for selectable dates
        selectConstraint: {
          start: this.allRide?.ride?.start_date ? moment(this.allRide.ride.start_date, 'DD-MM-YYYY').format('YYYY-MM-DD') : null,
          end: this.allRide?.ride?.end_date ? moment(this.allRide.ride.end_date, 'DD-MM-YYYY').add(1, 'days').format('YYYY-MM-DD') : null
        }
      };
    },
    
    // handleDragStart(info) {
    //   if (!info.event.id.startsWith('attraction-')) {
    //     info.preventDefault();
    //     return;
    //   }
      
    //   if (!this.isUserManager || !this.compareToUserId(this.allRide?.ride?.authorId)) {
    //     info.preventDefault();
    //     return;
    //   }
    // },

    handleEventDidMount(info) {
      info.el.dataset.attractionId = info.event.id;
    },

    handleEventDragStop(info) {
      this.stopDragTracking(); // clears DOM/listener; does NOT null _dragState

      // For early exits that are not cross-day drops, null _dragState here.
      if (!info.event.id.startsWith('attraction-')) { this._dragState = null; return; }
      if (this.isRideLocked) { this._dragState = null; return; }
      if (!this.compareToUserId(this.allRide?.ride?.authorId)) { this._dragState = null; return; }

      const dropX = info.jsEvent?.clientX ?? 0;
      const dropY = info.jsEvent?.clientY ?? 0;
      const elementAtPoint = document.elementFromPoint(dropX, dropY);
      const dayCell = elementAtPoint?.closest('.fc-day[data-date]');
      if (!dayCell) { this._dragState = null; return; }

      const dropDate = dayCell.dataset.date;

      // Compute original day from event ID — info.event.start is already updated
      // to the new date by the time eventDragStop fires, so comparing against it
      // would always match and fail to detect cross-day drops.
      const [, dayIndex, attractionIndex] = info.event.id.split('-');
      const startDate = moment(this.allRide.ride.start_date, 'DD-MM-YYYY');
      const originalDate = startDate.clone().add(parseInt(dayIndex), 'days').format('YYYY-MM-DD');

      if (dropDate !== originalDate) {
        // Cross-day drop — leave _dragState alive so handleEventDrop (which fires
        // after eventDragStop) can read it if needed.
        return;
      }

      // Same-day reorder — use _lastPlaceholderInsert which survives stopDragTracking
      const saved = this._lastPlaceholderInsert;
      this._lastPlaceholderInsert = null;
      this._dragState = null;

      if (!saved) return;
      const dayAttractions = this.allRide.attractionsByDay[parseInt(dayIndex)].daily_attractions;
      if (dayAttractions.length <= 1) return;

      const draggedIdx = parseInt(attractionIndex);
      const draggedAttraction = dayAttractions[draggedIdx];
      const otherAttractions = dayAttractions.filter((_, i) => i !== draggedIdx);

      const safeInsert = Math.min(saved.index, otherAttractions.length);
      const orderedAttractions = [...otherAttractions];
      orderedAttractions.splice(safeInsert, 0, draggedAttraction);

      const unchanged = orderedAttractions.every((a, i) => a.id === dayAttractions[i]?.id);
      if (unchanged) return;

      this.reorderAttractionsInDay({
        date: moment(info.event.start).format('M/D/YYYY'),
        orderedAttractions,
        movedAttractionId: draggedAttraction.id
      });
    },

    handleEventDragStart(info) {
      this._lastPlaceholderInsert = null;
      this._dragState = {
        rafId: null,
        currentDayCell: null,
        placeholder: null,
        handler: null,
        draggedEventId: info.event.id,
        lastInsertBefore: undefined
      };

      const handler = (e) => {
        const x = e.clientX ?? e.touches?.[0]?.clientX;
        const y = e.clientY ?? e.touches?.[0]?.clientY;
        if (x === undefined) return;
        if (this._dragState.rafId) cancelAnimationFrame(this._dragState.rafId);
        this._dragState.rafId = requestAnimationFrame(() => {
          this.updateDragVisual(x, y);
        });
      };

      this._dragState.handler = handler;
      document.addEventListener('mousemove', handler);
      document.addEventListener('touchmove', handler, { passive: true });
    },

    clearDragVisual() {
      const state = this._dragState;
      if (!state) return;

      if (state.placeholder?.parentNode) {
        state.placeholder.parentNode.removeChild(state.placeholder);
      }
      state.placeholder = null;
      state.lastInsertBefore = undefined;

      if (state.currentDayCell) {
        state.currentDayCell
          .querySelectorAll('.fc-daygrid-event-harness')
          .forEach(h => { h.style.transform = ''; });
      }
      state.currentDayCell = null;
    },

    updateDragVisual(x, y) {
      const state = this._dragState;
      if (!state) return;

      const elAtPoint = document.elementFromPoint(x, y);
      const dayCell = elAtPoint?.closest('.fc-day[data-date]');

      if (!dayCell) {
        this.clearDragVisual();
        return;
      }

      // Verify the cursor is actually within this cell's layout bounds.
      // translateY shifts on child events can extend them visually into
      // an adjacent row, causing elementFromPoint to return this cell's
      // events even when the cursor is over a different day — which
      // mismatches FullCalendar's own drop-target detection.
      const cellRect = dayCell.getBoundingClientRect();
      if (y < cellRect.top || y > cellRect.bottom || x < cellRect.left || x > cellRect.right) {
        this.clearDragVisual();
        return;
      }

      if (dayCell !== state.currentDayCell) {
        this.clearDragVisual();
        state.currentDayCell = dayCell;
      }

      const eventsContainer = dayCell.querySelector('.fc-daygrid-day-events');
      if (!eventsContainer) return;

      // Find all attraction events excluding the one being dragged, mapped to their harnesses
      const items = Array.from(
        dayCell.querySelectorAll('.fc-event[data-attraction-id^="attraction-"]')
      )
        .filter(el => el.dataset.attractionId !== state.draggedEventId)
        .map(el => {
          const harness = el.closest('.fc-daygrid-event-harness');
          if (!harness) return null;
          const rect = harness.getBoundingClientRect();
          return { harness, centerY: rect.top + rect.height / 2 };
        })
        .filter(Boolean)
        .sort((a, b) => a.centerY - b.centerY);

      // Determine insertion index
      let insertBefore = items.length;
      for (let i = 0; i < items.length; i++) {
        if (y < items[i].centerY) { insertBefore = i; break; }
      }

      // Skip if unchanged
      if (insertBefore === state.lastInsertBefore) return;
      state.lastInsertBefore = insertBefore;
      // Persist for handleEventDrop — survives clearDragVisual resets
      this._lastPlaceholderInsert = { date: dayCell.dataset.date, index: insertBefore };

      // Create placeholder div once
      if (!state.placeholder) {
        const ph = document.createElement('div');
        ph.className = 'drag-placeholder';
        state.placeholder = ph;
      }

      // Insert placeholder at the correct position in the events container
      const referenceEl = insertBefore < items.length ? items[insertBefore].harness : null;
      eventsContainer.insertBefore(state.placeholder, referenceEl);

      // Shift harnesses below the gap down by placeholder height (28px) + margin (4px)
      items.forEach((item, i) => {
        item.harness.style.transform = i >= insertBefore ? 'translateY(32px)' : '';
      });
    },

    stopDragTracking() {
      const state = this._dragState;
      if (!state) return;

      if (state.rafId) cancelAnimationFrame(state.rafId);
      if (state.handler) {
        document.removeEventListener('mousemove', state.handler);
        document.removeEventListener('touchmove', state.handler);
      }
      this.clearDragVisual();
      // _dragState is nulled by the caller — handleEventDrop (cross-day) nulls it
      // after reading it; handleEventDragStop (same-day) nulls it directly.
    },

    async handleEventDrop(info) {
      if (this.isRideLocked) {
        info.revert();
        this.showModalWithParameters({
          errorNumber: "uesr_is_not_authorize_to_edit_this_ride",
          type: 'textConverter'
        });
        return;
      }

      if (!info.event.id.startsWith('attraction-')) {
        info.revert();
        return;
      }

      const newDate = moment(info.event.start);
      const startDate = moment(this.allRide.ride.start_date, 'DD-MM-YYYY');
      const endDate = moment(this.allRide.ride.end_date, 'DD-MM-YYYY').endOf('day');

      if (newDate.isBefore(startDate) || newDate.isAfter(endDate)) {
        info.revert();
        this.showModalWithParameters({ errorNumber: 'day_not_in_trip_range', type: 'textConverter' });
        return;
      }

      const [, oldDayIndex, oldAttractionIndex] = info.event.id.split('-');
      const movedAttraction = this.allRide.attractionsByDay[parseInt(oldDayIndex)].daily_attractions[parseInt(oldAttractionIndex)];

      const newDateStr = newDate.format('YYYY-MM-DD');
      this._dragState = null;

      // Use the last recorded placeholder position — set by updateDragVisual every
      // time the placeholder moved, stored independently of _dragState so it
      // survives the eventDragStop cleanup that fires before eventDrop.
      const saved = this._lastPlaceholderInsert;
      this._lastPlaceholderInsert = null;

      // If the placeholder was in a different day than where FullCalendar registered
      // the drop (happens when translateY-shifted events spill into the next row and
      // fool FullCalendar's hit detection), trust the placeholder's day.
      const actualDateStr = saved?.date ?? newDateStr;
      const actualDate = moment(actualDateStr, 'YYYY-MM-DD');
      const actualDayIndex = actualDate.diff(startDate, 'days');
      const actualDayAttractions = this.allRide.attractionsByDay[actualDayIndex]?.daily_attractions || [];

      let orderedAttractions;
      if (actualDayAttractions.length > 0 && saved?.index !== undefined) {
        const insertBefore = Math.min(saved.index, actualDayAttractions.length);
        orderedAttractions = [...actualDayAttractions];
        orderedAttractions.splice(insertBefore, 0, movedAttraction);
      } else {
        orderedAttractions = [movedAttraction, ...actualDayAttractions];
      }

      try {
        await this.reorderAttractionsInDay({
          date: actualDate.format('M/D/YYYY'),
          orderedAttractions,
          movedAttractionId: movedAttraction.id
        });
      } catch (error) {
        info.revert();
      }
    },

    // Handle events being received from another calendar
    async handleEventReceive(info) {
      // Use the same logic as handleEventDrop
      await this.handleEventDrop(info);
    },

    async handleDateClick(info) {
      if (this.isRideLocked) {
        this.showModalWithParameters({
          errorNumber: "uesr_is_not_authorize_to_edit_this_ride",
          type: 'textConverter'
        });
        return;
      }
      
      // Find the day index for the clicked date
      const clickedDate = moment(info.date);
      const startDate = moment(this.allRide.ride.start_date, 'DD-MM-YYYY');
      const endDate = moment(this.allRide.ride.end_date, 'DD-MM-YYYY');
      const dayIndex = clickedDate.diff(startDate, 'days');

      // Check if the clicked date is within the ride's range
      if (clickedDate.isBefore(startDate) || clickedDate.isAfter(endDate)) {
        this.showModalWithParameters({
          errorNumber: 'day_not_in_trip_range',
          type: 'textConverter'
        });
        return;
      }

      // Only proceed if it's a valid day within the trip
      if (dayIndex >= 0 && dayIndex < this.allRide.attractionsByDay.length) {
        // Update markers for the clicked day
        // only if not mobile
        if (this.isNotMobile){
          await this.$store.dispatch('rideUpdateMarkerByDayIndex', dayIndex);
          this.$store.commit( 'rideSetRideFocusDay', dayIndex + 1 );
        }

        // For managers, show day management options (only if not locked)
        if (this.compareToUserId(this.allRide.ride.authorId) && !this.isRideLocked) {
          this.showModalWithParameters({
            inerModalParameters: { 
              date: info.date,
              dayIndex: dayIndex
            },
            type: 'dayManagerOptions'
          });
        } else if (this.isRideLocked) {
          this.showModalWithParameters({
            errorNumber: "uesr_is_not_authorize_to_edit_this_ride",
            type: 'textConverter'
          });
        }
      }
    },

    formatDate(date) {
      return moment(date).format('DD/MM/YYYY');
    },
    
    activAccommodation(dayActivities) {
      if (Object.keys(dayActivities.accommodation).length) {
        return dayActivities.accommodation;
      }
      return dayActivities.base_trip_accommodation;
    },

    isBookedAccommodation(dayActivities) {
      return this.activAccommodation(dayActivities).status === "booked";
    },

    isSuggestedAccommodation(dayActivities) {
      return this.activAccommodation(dayActivities).status === "base_trip_accomodation";
    },
    
    getEventsFromAttractions() {
      if (!this.allRide?.attractionsByDay) return [];
      
      let events = [];
      let processedAccommodations = new Set();

      this.allRide.attractionsByDay.forEach((dayActivities, dayIndex) => {
        // Add attractions
        dayActivities.daily_attractions.forEach((attraction, attractionIndex) => {
          // Use the day's datetime directly
          const startDate = moment.utc(dayActivities.dateTime).format('YYYY-MM-DD');

          // Extract and format time from attraction
          let timeStr = '';
          let timeStrForTitle = '';
          let startDateTime = '';
          let endDateTime = '';
          
          if (attraction.dateTime) {
            // Parse the full datetime string in UTC
            const dateTime = moment.utc(attraction.dateTime);
            timeStr = dateTime.format('HH:mm:ss') + ' ';
            if( this.isUserManager ) {
              timeStrForTitle = dateTime.format('HH:mm') + ' ';
            }
            
            // Create start and end times (30 seconds apart)
            startDateTime = `${startDate}T${timeStr.trim()}`;
            endDateTime = moment.utc(startDateTime).add(30, 'seconds').format('YYYY-MM-DDTHH:mm:ss');
          }
            
          events.push({
            id: `attraction-${dayIndex}-${attractionIndex}`,
            title: `${timeStrForTitle}${attraction.title}`,
            start: startDateTime,
            end: endDateTime,
            allDay: false,
            backgroundColor: '#07A0C3',
            borderColor: '#07A0C3',
            textColor: '#ffffff',
            display: 'block',
            editable: !this.isRideLocked && this.compareToUserId(this.allRide?.ride?.authorId), // Allow drag and drop for attractions only if not locked
            durationEditable: false, // Prevent resizing for attractions
            order: attractionIndex,
            extendedProps: {
              type: 'attraction',
              dayIndex,
              attractionIndex,
              attraction
            }
          });
        });

        // Handle accommodation
        const accommodation = this.activAccommodation(dayActivities);
        if (accommodation && Object.keys(accommodation).length > 0) {
          const accommodationId = accommodation.id || accommodation.name;
          
          if (!processedAccommodations.has(accommodationId)) {
            processedAccommodations.add(accommodationId);
            
            const shortenCheckIn = moment.utc(accommodation.check_in_date, 'DD-MM-YYYY').format('DD.MM');
            const shortenCheckOut = moment.utc(accommodation.check_out_date, 'DD-MM-YYYY').format('DD.MM');
            const checkIn = moment.utc(accommodation.check_in_date, 'DD-MM-YYYY').format('YYYY-MM-DD');
            const checkOut = moment.utc(accommodation.check_out_date, 'DD-MM-YYYY').format('YYYY-MM-DD');
            
            // Set different colors based on accommodation status
            const isBooked = this.isBookedAccommodation(dayActivities);
            const isSuggested = this.isSuggestedAccommodation(dayActivities);
            
            const accommodationColors = {
              backgroundColor: isBooked ? '#28a745' : '#FFA500', // Green for booked, Orange for suggested
              borderColor: isBooked ? '#28a745' : '#FFA500',
            };
            
            events.push({
              id: `accommodation-${accommodationId}`,
              title: `🏨 ${accommodation.name} ${shortenCheckIn}-${shortenCheckOut}${isBooked ? ` (${this.t('tripCalendarBooked')})` : ` (${this.t('tripCalendarNotBooked')})`}`,
              start: checkIn,
              end: checkOut,
              allDay: true,
              ...accommodationColors,
              textColor: '#ffffff',
              order: -999, // Very low order number to ensure it appears at the top
              editable: false, // Prevent drag and drop for accommodations
              durationEditable: false, // Prevent resizing for accommodations
              extendedProps: {
                type: 'accommodation',
                isBooked,
                isSuggested,
                accommodation
              }
            });
          }
        }
      });

      return events;
    },

    // Scheduled organizer messages rendered as calendar events, exactly like
    // attractions/accommodations above -- this keeps them reactive through
    // the same options-recompute pipeline instead of hand-rolled DOM syncing.
    // One extra synthetic "add a message" event per TRIP day gives each of
    // them its own inline affordance, matching the attraction/accommodation
    // rows it sits below (order is pushed past them so it always sorts
    // last). Deliberately the trip and nothing wider -- see the note inside
    // the function; the days either side are reached from the general
    // "create a scheduled message" button instead.
    getOrganizerMessageEvents() {
      if (!this.canManageOrganizerMessages) return [];

      const events = [];

      this.organizerMessages.forEach((message, index) => {
        const startDateTime = `${message.target_date}T${message.send_time}:00`;
        const endDateTime = moment(startDateTime).add(30, 'seconds').format('YYYY-MM-DDTHH:mm:ss');

        // A note to the manager himself gets the amber of the list's badge
        // (#fde9c8) instead of the group-message purple, so it is
        // unmistakable at a glance in a dense month view.
        const isPrivate = this.organizerMessageIsPrivate(message);

        events.push({
          id: `organizer-message-${message.id}`,
          title: message.body,
          start: startDateTime,
          end: endDateTime,
          allDay: false,
          backgroundColor: isPrivate ? '#fde9c8' : '#6c4fc7',
          borderColor: isPrivate ? '#e0a63a' : '#6c4fc7',
          textColor: isPrivate ? '#2E2E3A' : '#ffffff',
          display: 'block',
          editable: false,
          durationEditable: false,
          order: 1000 + index,
          extendedProps: { type: 'organizerMessage', message }
        });
      });

      // No per-day "+ add a message" affordance here any more.
      //
      // Owner's call, 2026-10-05: scheduling a message for a day now lives
      // in the day menu that a cell click already opens (DayManagerOptions
      // -- "הוספת פעילות", "הוספת לינה", "הוסף יום אחרי"), next to the
      // other per-day actions, instead of a row inside the cell. One row
      // back per day, and the action sits where a manager already looks for
      // per-day actions.
      //
      // Dates OUTSIDE the trip are still reached through the general
      // "create a scheduled message" button above the calendar, whose date
      // field spans the whole allowed window -- cells for those days do not
      // exist here at all (see the before/after blocks).

      return events;
    },

    openCreateOrganizerMessage() {
      // The general entry point: same modal, no day pre-chosen by a cell.
      // Called with no date on purpose -- the modal picks the default from
      // its own bounds, because it owns the date field. See openForDate.
      this.$refs.organizerMessageModal.openForDate();
    },

    renderEventContent(eventInfo) {
      const event = eventInfo.event;

      // Scheduled organizer message: envelope + time + truncated body,
      // with its own edit/delete icons -- mirrors the attraction branch's
      // time-edit-icon below (stopPropagation keeps handleEventClick from
      // also firing on the same click).
      if (event.extendedProps.type === 'organizerMessage') {
        const message = event.extendedProps.message;

        const container = document.createElement('div');
        container.className = 'fc-event-main-content organizer-message-event';
        if (this.organizerMessageIsPrivate(message)) {
          container.classList.add('organizer-message-event--private');
        }
        container.style.width = '100%';
        container.style.minWidth = '0';

        const contentWrapper = document.createElement('div');
        contentWrapper.className = 'event-content-wrapper organizer-message-content';
        container.appendChild(contentWrapper);

        const titleDiv = document.createElement('div');
        titleDiv.className = 'fc-event-title organizer-message-title';
        titleDiv.style.minWidth = '0';
        const timeLabel = moment(message.send_time, 'HH:mm').format('HH:mm');
        const truncatedBody = message.body.length > 40
          ? `${message.body.slice(0, 40)}…`
          : message.body;
        // The recipient label sits beside the time, not in the body's 40
        // characters, and comes from the same helper the list uses.
        titleDiv.textContent = `✉ ${timeLabel} · ${this.organizerRecipientLabel(message)}  ${truncatedBody}`;
        contentWrapper.appendChild(titleDiv);

        const iconWrapper = document.createElement('div');
        iconWrapper.className = 'time-icon-wrapper organizer-message-icons';

        const editIcon = document.createElement('span');
        editIcon.className = 'material-icons organizer-message-icon';
        editIcon.innerHTML = 'edit';
        editIcon.title = this.t('organizerMessageEditAction');
        editIcon.style.cursor = 'pointer';
        editIcon.onclick = (e) => {
          e.stopPropagation();
          this.$refs.organizerMessageModal.openForMessage(message);
          return false;
        };
        iconWrapper.appendChild(editIcon);

        const deleteIcon = document.createElement('span');
        deleteIcon.className = 'material-icons organizer-message-icon';
        deleteIcon.innerHTML = 'close';
        deleteIcon.title = this.t('organizerMessageDeleteAction');
        deleteIcon.style.cursor = 'pointer';
        deleteIcon.onclick = (e) => {
          e.stopPropagation();
          this.deleteOrganizerMessage(message);
          return false;
        };
        iconWrapper.appendChild(deleteIcon);

        contentWrapper.appendChild(iconWrapper);
        return { domNodes: [container] };
      }

      // For attractions, add a time edit button
      if (event.extendedProps.type === 'attraction') {
        const timeIcon = document.createElement('span');
        timeIcon.className = 'material-icons time-edit-icon';
        timeIcon.innerHTML = 'schedule';
        timeIcon.style.cursor = 'pointer';
        timeIcon.style.marginInlineStart = '5px';
        
        // Only show and enable time edit icon if ride is not locked
        if (!this.isRideLocked && this.compareToUserId(this.allRide?.ride?.authorId)) {
          timeIcon.onclick = (e) => {
            e.stopPropagation();
            this.openUpdateAttractionTime(event.extendedProps.attraction);
            return false; // Prevent event bubbling
          };
        } else {
          timeIcon.style.opacity = '0.3';
          timeIcon.style.cursor = 'not-allowed';
          timeIcon.onclick = null;
        }

        const container = document.createElement('div');
        container.className = 'fc-event-main-content';
        container.style.width = '100%';
        container.style.minWidth = '0';
        
        const contentWrapper = document.createElement('div');
        contentWrapper.className = 'event-content-wrapper';
        container.appendChild(contentWrapper);
        
        const titleDiv = document.createElement('div');
        titleDiv.className = 'fc-event-title';
        titleDiv.style.minWidth = '0';
        titleDiv.style.overflow = 'visible';
        titleDiv.textContent = event.title;
        contentWrapper.appendChild(titleDiv);
        
        const iconWrapper = document.createElement('div');
        iconWrapper.className = 'time-icon-wrapper';
        iconWrapper.appendChild(timeIcon);
        contentWrapper.appendChild(iconWrapper);
        
        return { domNodes: [container] };
      }
      
      // For accommodations, use default rendering
      return { html: `<div class="fc-event-title">${event.title}</div>` };
    },

    openUpdateAttractionTime(attraction) {
      if (this.isRideLocked) {
        this.$store.dispatch("showModalWithParameters", { 
          errorNumber: "uesr_is_not_authorize_to_edit_this_ride",
          type: 'textConverter' 
        });
        return;
      }
      this.$store.commit("setAttractionForChangeingTheDate", attraction);
      if (this.compareToUserId(this.allRide.ride.authorId)) {
        this.$store.dispatch("showModal", "updateAttractionTime");
      } else {
        this.$store.dispatch("showModalWithParameters", { 
          errorNumber: "uesr_is_not_authorize_to_edit_this_ride",
          type: 'textConverter' 
        });
      }
    },

    handleEventClick(info) {
      const event = info.event;
      
      if (event.extendedProps.type === 'attraction') {
        const attraction = event.extendedProps.attraction;
        
        // For ride managers, show options menu (only if not locked)
        if (this.isUserManager && this.compareToUserId(this.allRide.ride.authorId) && !this.isRideLocked) {
          this.showModalWithParameters({
            inerModalParameters: { attraction },
            type: 'attractionManagerOptions'
          });
        } else if (this.isRideLocked) {
          // If locked, show error message instead of options
          this.showModalWithParameters({
            errorNumber: "uesr_is_not_authorize_to_edit_this_ride",
            type: 'textConverter'
          });
        } else {
          // For all users, show attraction information popup
          this.$store.dispatch('lowedAttractionAndShow', {
            attractionId: attraction.id,
            sorce: 'internal'
          });
        }
      } else if (event.extendedProps.type === 'accommodation') {
        // Convert the ID from the event to a number since accommodation.id is a number
        const clickedAccommodationId = Number(event.id.split('-')[1]);
        
        // For booked accommodations, go to accommodation edit page
        if (event.extendedProps.isBooked && 
            this.compareToUserId(this.allRide.ride.authorId) &&
            !this.isRideLocked) {
          // Find the accommodation in the attractionsByDay array
          for (const [, dayActivities] of this.allRide.attractionsByDay.entries()) {
            if (dayActivities.accommodation && dayActivities.accommodation.id === clickedAccommodationId) {
              this.$store.dispatch('goToRideAccommodation', dayActivities.accommodation);
              return;
            }
          }
        } 
        // For non-booked accommodations when user is owner
        else if (!event.extendedProps.isBooked && 
                 this.compareToUserId(this.allRide.ride.authorId) &&
                 !this.isRideLocked) {
          // Find the accommodation in the attractionsByDay array
          if(!this.isUserManager)
            this.showModalWithParameters({
              errorNumber: 'unbooked_accommodation_info',
              type: 'textConverter'
            });
          for (const [dayIndex, dayActivities] of this.allRide.attractionsByDay.entries()) {
            if (dayActivities.base_trip_accommodation && 
                dayActivities.base_trip_accommodation.id === clickedAccommodationId) {
              this.$store.dispatch('goToBaseRideAccommodation', {
                baseRideAccommodation: dayActivities.base_trip_accommodation,
                dayIndex: dayIndex
              });
              return;
            }
          }
        }
        
        // For non-managers or non-owners, scroll to the accommodation section
        const element = document.getElementById(`${clickedAccommodationId}_acommodation_ref_id`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    },

    handleDayCellMount(arg) {
      const cellDate = moment(arg.date);
      const startDate = moment(this.allRide?.ride?.start_date, 'DD-MM-YYYY');
      const endDate = moment(this.allRide?.ride?.end_date, 'DD-MM-YYYY');

      // If date is outside trip range
      if (cellDate.isBefore(startDate) || cellDate.isAfter(endDate)) {
        arg.el.classList.add('outside-trip-range');
      }
    },
  },
  created() {
    // Remove the scroll event listener if we're in calendar view
    if (this.tripViewMode === 'calendar') {
      window.removeEventListener('scroll', this.rideUpdateMarkerByScrollerLocation);
    }
  },
  watch: {
    // The day menu (DayManagerOptions.vue) is a global modal and cannot
    // reach this component's $refs, so it writes the chosen day to the
    // store and this picks it up. See organizerMessageDayRequest in
    // store/modules/popups.js.
    //
    // Guarded on the gate as well as the ref: the modal is rendered
    // v-if="canManageOrganizerMessages", so $refs.organizerMessageModal is
    // undefined for everyone else and a stale request must not throw.
    organizerMessageDayRequest(request) {
      if (!request || !request.dateStr) return;
      if (!this.canManageOrganizerMessages) return;
      // The day menu is a b-modal closing as this fires; let it finish so
      // the two do not fight over focus and the backdrop.
      this.$nextTick(() => {
        if (this.$refs.organizerMessageModal) {
          this.$refs.organizerMessageModal.openForDate(request.dateStr);
        }
      });
    },

    tripViewMode(newMode) {
      // Add/remove scroll listener based on view mode
      if (newMode === 'calendar') {
        window.removeEventListener('scroll', this.rideUpdateMarkerByScrollerLocation);
      } else {
        window.addEventListener('scroll', this.rideUpdateMarkerByScrollerLocation);
      }
    },

    // Watch for changes in trip dates
    'allRide.ride.start_date': {
      handler() {
        this.updateCalendarDates();
      }
    },
    'allRide.ride.end_date': {
      handler() {
        this.updateCalendarDates();
      }
    },

    // Replaced by the organizerRideShape watcher in
    // organizerMessagesMixin.js -- see the note there.
  }
};
</script>

<style scoped>
.trip-calendar-section {
  padding: 2rem 0;
  position: relative;
  overflow: hidden;
}

.calendar-instructions {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1rem;
  margin: 0 1rem 1rem;
  display: flex;
  flex-direction: row;
  justify-content: center;
  gap: 2rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.instruction-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.1rem;
  color: #2E2E3A;
}

.instruction-item i {
  color: #07A0C3;
  font-size: 1.2rem;
}

.instruction-item .material-icons {
  color: #07A0C3;
  font-size: 1.2rem;
}

@media (max-width: 768px) {
  .calendar-instructions {
    flex-direction: column;
    gap: 1rem;
    align-items: center;
    text-align: center;
    padding: 1rem 0.5rem;
  }
}

.calendars-container {
  display: flex;
  flex-direction: row;
  gap: 2rem;
  justify-content: center;
  flex-wrap: wrap;
  margin: 0 1rem;
  width: 100%;
}

.calendar-wrapper {
  background: #ffffff;
  border-radius: 1rem;
  padding: 1rem;
  box-shadow: 0 0 15px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
}

.trip-calendar {
  height: auto;
}

:deep(.fc) {
  font-size: 1.1rem;
  height: auto !important;
}

[dir="rtl"] :deep(.fc) {
  direction: rtl;
}

[dir="ltr"] :deep(.fc) {
  direction: ltr;
}

:deep(.fc-view-harness) {
  height: auto !important;
}

:deep(.fc-toolbar-title) {
  font-size: 2rem !important;
}

:deep(.fc-button) {
  font-size: 1.2rem !important;
}

:deep(.fc-event) {
  cursor: pointer;
  padding: 0.4rem 0.5rem;
  border-radius: 4px;
  font-size: 1.1rem !important;
  min-height: 28px;
  height: auto !important;
  white-space: normal;
  line-height: 1.3;
}

:deep(.fc-event-title) {
  font-weight: 500;
  padding: 2px 0;
  white-space: normal !important;
  min-width: 0;
  flex: 1;
  font-size: 1.1rem;
}

:deep(.fc-daygrid-day-number) {
  font-size: 1.2rem;
  font-weight: 500;
}

/* Responsive adjustments */
@media (max-width: 900px) {
  .calendar-wrapper {
    max-width: 100%;
    padding: 0.5rem;
  }
  
  .calendars-container {
    flex-direction: column;
    gap: 1rem;
    margin: 0 0.5rem;
  }
  
  :deep(.fc) {
    font-size: 0.9rem !important;
  }
  
  :deep(.fc-toolbar-title) {
    font-size: 1.4rem !important;
  }
  
  :deep(.fc-button) {
    padding: 0.3rem 0.6rem !important;
    font-size: 0.9rem !important;
  }
  
  :deep(.fc-event) {
    padding: 0.2rem 0.3rem;
    font-size: 0.8rem !important;
    min-height: 24px;
  }
  
  :deep(.fc-event-title) {
    font-size: 0.8rem;
    margin-bottom: 2px;
  }
  
  :deep(.fc-daygrid-day-number) {
    font-size: 0.9rem;
  }

  :deep(.drag-placeholder) {
    height: 24px;
    margin-bottom: 3px;
  }
}

:deep(.fc-event:hover) {
  opacity: 0.9;
}

:deep(.fc-button-primary) {
  background-color: #07A0C3 !important;
  border-color: #07A0C3 !important;
  padding: 0.5rem 1rem;
}

:deep(.fc-button-primary:hover) {
  background-color: #0588a6 !important;
  border-color: #0588a6 !important;
}

:deep(.fc-event) {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

:deep(.fc-event-time),
:deep(.fc-event-title) {
  padding: 3px 4px;
  line-height: 1.3;
}

:deep(.fc-event.fc-event-draggable) {
  cursor: move;
  touch-action: none;
}

:deep(.fc-event-time) {
  display: none !important;
}

:deep(.fc-event[id^="accommodation-"]) {
  cursor: not-allowed !important;
}

:deep(.time-edit-icon) {
  font-size: 14px;
  color: white;
  opacity: 0.8;
  transition: opacity 0.2s;
  display: flex;
  align-items: center;
  margin-inline-start: 2px;
}

:deep(.time-edit-icon:hover) {
  opacity: 1;
}

:deep(.fc-event-main-content) {
  width: 100%;
}

[dir="rtl"] :deep(.event-content-wrapper) {
  display: flex;
  align-items: center;
  direction: rtl;
}

[dir="ltr"] :deep(.event-content-wrapper) {
  display: flex;
  align-items: center;
  direction: ltr;
}

:deep(.time-icon-wrapper) {
  display: flex;
  align-items: center;
}

@media (max-width: 768px) {
  :deep(.event-content-wrapper) {
    flex-direction: column;
    align-items: flex-start;
  }
  
  [dir="rtl"] :deep(.time-icon-wrapper) {
    width: 100%;
    justify-content: flex-start;
    margin-top: 2px;
  }

  [dir="ltr"] :deep(.time-icon-wrapper) {
    width: 100%;
    justify-content: flex-end;
    margin-top: 2px;
  }
}

:deep(.fc-day) {
  cursor: pointer;
}

:deep(.drag-placeholder) {
  height: 28px;
  border: 2px dashed #07A0C3;
  border-radius: 4px;
  background: rgba(7, 160, 195, 0.08);
  margin-bottom: 4px;
  pointer-events: none;
}

:deep(.fc-daygrid-event-harness) {
  transition: transform 0.15s ease;
}

:deep(.fc-daygrid-day-events) {
  padding-top: 16px;
  padding-bottom: 32px;
}

:deep(.fc-day:hover) {
  background-color: rgba(7, 160, 195, 0.1);
}

:deep(.fc-scroller) {
  overflow: hidden !important;
  height: auto !important;
}

:deep(.fc-scroller-liquid-absolute) {
  position: relative !important;
  top: auto !important;
  left: auto !important;
  right: auto !important;
  bottom: auto !important;
}

/* Manager options menu styles */
:deep(.manager-options-menu) {
  font-family: var(--primary-font-family, -apple-system, system-ui, BlinkMacSystemFont);
  font-size: 0.9rem;
}

:deep(.manager-option-item) {
  transition: background-color 0.2s ease;
  color: var(--primary-text-color, #2E2E3A);
}

:deep(.manager-option-item:hover) {
  background-color: var(--primary-background-hover, #f5f5f5);
}

:deep(.manager-option-item:active) {
  background-color: var(--primary-background-active, #e5e5e5);
}

/* Responsive adjustments */
@media (max-width: 768px) {
  :deep(.manager-options-menu) {
    font-size: 0.85rem;
    min-width: 130px;
  }
  
  :deep(.manager-option-item) {
    padding: 6px 12px;
  }
}

/* Styles for dates outside trip range */
:deep(.outside-trip-range) {
  background-color: #f5f5f5 !important;
  opacity: 0.7;
  cursor: not-allowed !important;
  position: relative;
}

:deep(.outside-trip-range)::after {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 24px;
  height: 4px;
  background-color: #999;
  border-radius: 2px;
  z-index: 1;
  pointer-events: none;
}

:deep(.outside-trip-range .fc-daygrid-day-frame) {
  opacity: 0.5;
}

:deep(.outside-trip-range .fc-daygrid-day-number) {
  color: #999 !important;
  position: relative;
  z-index: 2;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  :deep(.manager-options-menu) {
    font-size: 0.85rem;
    min-width: 130px;
  }
  
  :deep(.manager-option-item) {
    padding: 6px 12px;
  }

  :deep(.outside-trip-range)::after {
    width: 18px; /* Smaller dash for mobile */
    height: 3px;
  }
}

@media (max-width: 480px) {
  :deep(.outside-trip-range)::after {
    width: 14px; /* Even smaller dash for very small screens */
    height: 2px;
  }
}

/* Add a visual indicator for the current trip range */
:deep(.fc-day:not(.outside-trip-range)) {
  background-color: #ffffff;
  position: relative;
}

:deep(.fc-day:not(.outside-trip-range)):before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background-color: transparent;
}

:deep(.fc-day:not(.outside-trip-range)):hover:before {
  background-color: #07A0C3;
}

/* Make events more visible on non-disabled days */
:deep(.fc-day:not(.outside-trip-range) .fc-event) {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
  transition: transform 0.2s ease;
}

:deep(.fc-day:not(.outside-trip-range) .fc-event:hover) {
  transform: translateY(-1px);
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.2);
}

/* Organizer messages (scheduled WhatsApp notes) and the per-day "add a
   message" affordance. All sizes in px -- html{font-size:10px} in this app
   makes rem misleading. */
:deep(.organizer-message-event) {
  width: 100%;
}

[dir="rtl"] :deep(.organizer-message-content) {
  display: flex;
  align-items: center;
  direction: rtl;
}

[dir="ltr"] :deep(.organizer-message-content) {
  display: flex;
  align-items: center;
  direction: ltr;
}

:deep(.organizer-message-title) {
  white-space: normal;
  overflow-wrap: anywhere;
}

:deep(.organizer-message-icons) {
  display: flex;
  align-items: center;
  gap: 4px;
}

:deep(.organizer-message-icon) {
  font-size: 14px;
  color: white;
  opacity: 0.85;
  transition: opacity 0.2s;
  margin-inline-start: 2px;
}

:deep(.organizer-message-icon:hover) {
  opacity: 1;
}

/* A private note: the chip's amber background comes from the event colours;
   dark text and icons keep it readable on it, and bold marks it further. */
:deep(.organizer-message-event--private) {
  color: #2E2E3A;
  font-weight: 600;
}

:deep(.organizer-message-event--private .organizer-message-icon) {
  color: #2E2E3A;
}

@media (max-width: 768px) {
  :deep(.organizer-message-icon) {
    font-size: 13px;
  }
}

.organizer-message-create-row {
  /* px, not rem: this app sets html { font-size: 10px }, so rem reads wrong. */
  display: flex;
  justify-content: flex-end;
  margin-bottom: 10px;
}

.organizer-message-create-button {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border: 1px solid #6c4fc7;
  border-radius: 6px;
  background: #ffffff;
  color: #6c4fc7;
  font-size: 14px;
  cursor: pointer;
}

.organizer-message-create-button:hover {
  background: #6c4fc7;
  color: #ffffff;
}

.organizer-message-create-button .material-icons {
  font-size: 18px;
}
</style> 